/**
 * Transform functions to convert Template Media API data
 * to the format expected by BaseTemplate component
 */

import type { VideoActionButton } from '@/components/MuxVideoPlayer';

/**
 * Template Media from API
 */
export interface ApiTemplateMedia {
  id: number;
  type: 'video' | 'photo';
  src?: string;
  playback_id?: string;
  title: string;
  description?: string;
  action_button_url?: string;
  action_button_text?: string;
  is_active: boolean;
  display_order: number;
  template_name?: string;
  country_code?: string;
}

/**
 * Media Item format expected by BaseTemplate
 */
export interface BaseTemplateMediaItem {
  id: number;
  type: 'video' | 'photo';
  src: string;
  title: string;
  playbackId?: string;
  actionButton?: VideoActionButton;
}

/**
 * Transform API media to BaseTemplate format
 * 
 * Transformations:
 * - For videos: Generate src from playback_id if not present
 * - Map playback_id -> playbackId (camelCase)
 * - Map action_button_url + action_button_text -> actionButton object
 */
export function transformApiMediaToBaseTemplate(
  apiMedia: ApiTemplateMedia[]
): BaseTemplateMediaItem[] {
  return apiMedia.map((item) => {
    const transformed: BaseTemplateMediaItem = {
      id: item.id,
      type: item.type,
      src: item.src || '',
      title: item.title
    };

    // For videos: ensure src is generated from playback_id
    if (item.type === 'video' && item.playback_id) {
      transformed.playbackId = item.playback_id;
      
      // Generate src if not provided
      if (!item.src) {
        transformed.src = `https://image.mux.com/${item.playback_id}/thumbnail.jpg`;
      }
    }

    // Transform action button
    if (item.action_button_url && item.action_button_text) {
      transformed.actionButton = {
        label: item.action_button_text,
        url: item.action_button_url,
        openInNewTab: true
      };
    }

    return transformed;
  });
}

/**
 * Transform BaseTemplate media to API format
 * 
 * Use this when you want to programmatically create media items
 * from the BaseTemplate format
 */
export function transformBaseTemplateToApiMedia(
  baseMedia: BaseTemplateMediaItem
): Omit<ApiTemplateMedia, 'id' | 'created_at' | 'updated_at'> {
  const transformed: Record<string, unknown> = {
    type: baseMedia.type,
    title: baseMedia.title,
    is_active: true,
    display_order: 0
  };

  // For videos: only send playback_id, src will be auto-generated
  if (baseMedia.type === 'video' && baseMedia.playbackId) {
    transformed.playback_id = baseMedia.playbackId;
  }
  // For photos: send src (full URL)
  else if (baseMedia.type === 'photo') {
    transformed.src = baseMedia.src;
  }

  // Transform action button
  if (baseMedia.actionButton) {
    transformed.action_button_url = baseMedia.actionButton.url;
    transformed.action_button_text = baseMedia.actionButton.label;
  }

  return transformed;
}
